import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

const Login = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { session } = useAuth();
  const { slug } = useParams();
  const forgotPasswordPath = slug ? `/${slug}/forgot-password` : "/forgot-password";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (session) {
      navigate("/dashboard");
    }
  }, [session, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Vérifier si l'email est confirmé
      if (data.user && !data.user.email_confirmed_at) {
        // Envoyer un nouveau code de vérification via noreply@piktable.ch
        await supabase.functions.invoke('send-verification-code', {
          body: {
            userId: data.user.id,
            email: data.user.email,
          }
        });

        toast({
          title: "Vérification requise",
          description: "Un code de vérification a été envoyé à votre email.",
        });
        
        // Rediriger vers la page de vérification
        navigate("/verify-email", { 
          state: { 
            email: data.user.email, 
            userId: data.user.id 
          } 
        });
        return;
      }

      toast({
        title: "Connexion réussie",
        description: "Redirection vers le dashboard...",
      });

      navigate("/dashboard");
    } catch (error: any) {
      let errorMessage = "Email ou mot de passe incorrect";
      
      // Traduire les erreurs Supabase en français
      if (error.message?.includes("Email not confirmed")) {
        errorMessage = "Email non confirmé. Veuillez vérifier votre boîte mail.";
      } else if (error.message?.includes("Invalid login credentials")) {
        errorMessage = "Email ou mot de passe incorrect.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erreur de connexion",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-accent/5">
      <div className="w-full max-w-md px-4">
        <Button asChild variant="ghost" className="mb-4">
          <Link to="/">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Link>
        </Button>
        <Card className="p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-serif font-bold mb-2">Administration</h1>
            <p className="text-muted-foreground">Connectez-vous pour gérer les réservations</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@exemple.com" 
                required 
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Input 
                  id="password" 
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required 
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex justify-end -mt-2">
              <Link
                to={forgotPasswordPath}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Mot de passe oublié ?
              </Link>
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Login;
